import { useCallback, useEffect, useState } from 'react';
import { Bluetooth, Printer, RefreshCw, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { getPairedPrinter, PRINTER_SLOT_LABELS } from '../lib/thermalPrint/printerSlots';
import {
  isWebBluetoothSupported,
  pairBluetoothPrinter,
  printEscPosLines,
  unpairBluetoothPrinter,
  warmReconnectPrinter,
  isPrinterSessionActive,
} from '../lib/thermalPrint/webBluetoothPrinter';
import { centerLine } from '../lib/thermalPrint/escPos';

export default function PrinterSlotPanel({ slot = 'kitchen', compact = false, className = '' }) {
  const [paired, setPaired] = useState(() => getPairedPrinter(slot));
  const [busy, setBusy] = useState(false);
  const [bleReady, setBleReady] = useState(() => isPrinterSessionActive(slot));
  const supported = isWebBluetoothSupported();

  const sync = useCallback(() => {
    setPaired(getPairedPrinter(slot));
    setBleReady(isPrinterSessionActive(slot));
  }, [slot]);

  useEffect(() => {
    // Al montar (cambio de pantalla): reconectar en silencio si estaba emparejada
    if (getPairedPrinter(slot) && !isPrinterSessionActive(slot)) {
      warmReconnectPrinter(slot).then(() => sync()).catch(() => {});
    }
  }, [slot, sync]);

  useEffect(() => {
    sync();
    const t = window.setInterval(sync, 4000);
    return () => window.clearInterval(t);
  }, [sync]);

  const onPair = async () => {
    if (!supported) {
      toast.error('Bluetooth no disponible. Usa Chrome en Android o PC.');
      return;
    }
    setBusy(true);
    try {
      await pairBluetoothPrinter(slot);
      sync();
      toast.success('Impresora emparejada');
    } catch (err) {
      toast.error(err?.message || 'No se pudo emparejar');
    } finally {
      setBusy(false);
    }
  };

  const onReconnect = async () => {
    setBusy(true);
    try {
      const ok = await warmReconnectPrinter(slot, { force: true });
      sync();
      if (ok) toast.success('Reconectada');
      else toast.warning('No se pudo reconectar');
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setBusy(true);
    try {
      await printEscPosLines(slot, [
        centerLine('*** PRUEBA PIZAS ***'),
        centerLine(PRINTER_SLOT_LABELS[slot] || slot),
        centerLine(new Date().toLocaleString('es-MX')),
        '',
      ]);
      toast.success('Ticket de prueba enviado');
    } catch (err) {
      toast.error(err?.message || 'Error al imprimir');
    } finally {
      setBusy(false);
    }
  };

  const onUnpair = async () => {
    await unpairBluetoothPrinter(slot);
    sync();
    toast.info('Impresora desvinculada');
  };

  const statusColor = !paired ? 'text-slate-500' : bleReady ? 'text-emerald-600' : 'text-amber-600';

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        <Printer className={`w-4 h-4 ${statusColor}`} />
        <span className={`text-xs ${statusColor}`}>
          {paired ? (bleReady ? 'BLE OK' : 'Emparejada') : 'Sin impresora'}
        </span>
        {paired ? (
          <>
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onReconnect} title="Reconectar">
              <RefreshCw className="w-3 h-3 mr-1" /> Reconectar
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onUnpair} title="Quitar impresora para volver a emparejar">
              <Unlink className="w-3 h-3 mr-1" /> Quitar
            </Button>
          </>
        ) : (
          <Button type="button" size="sm" variant="outline" disabled={busy || !supported} onClick={onPair} title="Emparejar impresora">
            <Bluetooth className="w-3 h-3 mr-1" /> Emparejar
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-border bg-white p-4 space-y-3 ${className}`} data-testid={`printer-slot-${slot}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">{PRINTER_SLOT_LABELS[slot] || slot}</p>
          <p className={`text-xs ${statusColor}`}>
            {paired ? `${paired.deviceName} · ${bleReady ? 'conectada' : 'emparejada'}` : 'Sin emparejar'}
          </p>
        </div>
        <Printer className={`w-5 h-5 ${statusColor}`} />
      </div>
      {!supported && (
        <p className="text-xs text-amber-700">Web Bluetooth requiere Chrome en HTTPS (Android/PC).</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={onPair} disabled={busy || !supported}>
          <Bluetooth className="w-3.5 h-3.5 mr-1" /> Emparejar
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onReconnect} disabled={busy || !paired}>
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reconectar
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onTest} disabled={busy || !paired}>
          Probar
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onUnpair} disabled={!paired}>
          <Unlink className="w-3.5 h-3.5 mr-1" /> Quitar
        </Button>
      </div>
    </div>
  );
}
